import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import type {
  ListingExclusionRule,
  ListingOperator,
  ListingRule,
  ListingRuleCondition,
  ListingRuleField,
} from '@techgitter/contracts';

// Mirrors VALID_FIELDS in core/services/feed_rule_engine.py and
// ListingRuleField in contracts/src/contracts/distiListing.ts. Order is
// chosen for the dropdown — identity first, then taxonomy, pricing, stock,
// links — to put the most-used fields at the top.
const FIELD_OPTIONS: ListingRuleField[] = [
  'vendorSku',
  'manufacturer',
  'manufacturerCode',
  'description',
  'category',
  'subcategory',
  'subcategory1',
  'subcategory2',
  'subcategory3',
  'spec',
  'warranty',
  'cost',
  'rrpEx',
  'rrpInc',
  'shipping',
  'priceUnknown',
  'stock',
  'stockQld',
  'stockNsw',
  'stockSa',
  'url',
  'imageUrl',
];

const OP_OPTIONS: { op: ListingOperator; label: string }[] = [
  { op: 'eq', label: 'equals' },
  { op: 'ne', label: 'does not equal' },
  { op: 'in', label: 'is one of' },
  { op: 'not_in', label: 'is not one of' },
  { op: 'contains', label: 'contains' },
  { op: 'contains_all', label: 'contains all of' },
  { op: 'contains_any', label: 'contains any of' },
  { op: 'regex', label: 'matches regex' },
  { op: 'is_null', label: 'is empty' },
  { op: 'is_not_null', label: 'is not empty' },
];

const OPS_WITH_SINGLE_VALUE: ReadonlySet<ListingOperator> = new Set([
  'eq',
  'ne',
  'contains',
  'regex',
]);
const OPS_WITH_VALUES_LIST: ReadonlySet<ListingOperator> = new Set([
  'in',
  'not_in',
  'contains_all',
  'contains_any',
]);
const OPS_WITHOUT_INPUT: ReadonlySet<ListingOperator> = new Set([
  'is_null',
  'is_not_null',
]);

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

type Kind = 'classification' | 'exclusion';

type RuleListEditorProps =
  | {
      kind: 'classification';
      value: ListingRule[];
      onChange: (next: ListingRule[]) => void;
      onValidityChange?: (valid: boolean) => void;
    }
  | {
      kind: 'exclusion';
      value: ListingExclusionRule[];
      onChange: (next: ListingExclusionRule[]) => void;
      onValidityChange?: (valid: boolean) => void;
    };

export const RuleListEditor: React.FC<RuleListEditorProps> = (props) => {
  const { kind, value, onValidityChange } = props;

  const [mode, setMode] = useState<'form' | 'json'>('form');
  const [jsonText, setJsonText] = useState<string>(() =>
    JSON.stringify(value, null, 2),
  );
  const [jsonError, setJsonError] = useState<string | null>(null);

  // Re-sync the textarea when the canonical value changes from outside
  // (e.g. parent reloads after save). Only pulls down when in form mode —
  // otherwise the user's in-flight JSON edits would be wiped.
  const lastSyncedRef = useRef<string>('');
  useEffect(() => {
    if (mode === 'form') {
      const next = JSON.stringify(value, null, 2);
      if (next !== lastSyncedRef.current) {
        setJsonText(next);
        lastSyncedRef.current = next;
        setJsonError(null);
      }
    }
  }, [value, mode]);

  const valid = mode === 'form' || jsonError === null;
  useEffect(() => {
    onValidityChange?.(valid);
  }, [valid, onValidityChange]);

  const handleModeChange = (next: 'form' | 'json') => {
    if (next === mode) return;
    if (next === 'json') {
      // Snapshot current value into the textarea on entry.
      setJsonText(JSON.stringify(value, null, 2));
      setJsonError(null);
      setMode('json');
    } else {
      // Refuse to leave JSON mode while the text doesn't parse — the form
      // editor would have nothing safe to render.
      if (jsonError !== null) return;
      setMode('form');
    }
  };

  const handleJsonChange = (next: string) => {
    setJsonText(next);
    try {
      const parsed = JSON.parse(next || '[]');
      if (!Array.isArray(parsed)) {
        setJsonError('Rules must be a JSON array.');
        return;
      }
      setJsonError(null);
      // Cast through unknown — runtime shape is enforced by the engine on
      // save / preview, and the form editor will surface invalid entries
      // visually if the JSON pasted in doesn't match the schema.
      if (props.kind === 'classification') {
        props.onChange(parsed as ListingRule[]);
      } else {
        props.onChange(parsed as ListingExclusionRule[]);
      }
    } catch (err) {
      setJsonError(err instanceof Error ? err.message : 'Invalid JSON.');
    }
  };

  // --- Form-mode mutations ---------------------------------------------

  const updateRuleAt = (idx: number, next: ListingRule | ListingExclusionRule) => {
    const arr = [...value];
    // Type assertion is safe: callers only ever produce a rule of the
    // matching kind for the editor they're rendered in.
    arr[idx] = next as never;
    if (props.kind === 'classification') {
      props.onChange(arr as ListingRule[]);
    } else {
      props.onChange(arr as ListingExclusionRule[]);
    }
  };

  const removeRuleAt = (idx: number) => {
    const arr = value.filter((_, i) => i !== idx);
    if (props.kind === 'classification') {
      props.onChange(arr as ListingRule[]);
    } else {
      props.onChange(arr as ListingExclusionRule[]);
    }
  };

  const moveRule = (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= value.length) return;
    const arr = [...value];
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    if (props.kind === 'classification') {
      props.onChange(arr as ListingRule[]);
    } else {
      props.onChange(arr as ListingExclusionRule[]);
    }
  };

  const duplicateRule = (idx: number) => {
    const arr = [...value];
    const copy = JSON.parse(JSON.stringify(arr[idx]));
    arr.splice(idx + 1, 0, copy);
    if (props.kind === 'classification') {
      props.onChange(arr as ListingRule[]);
    } else {
      props.onChange(arr as ListingExclusionRule[]);
    }
  };

  const addRule = () => {
    if (props.kind === 'classification') {
      const next: ListingRule[] = [
        ...(value as ListingRule[]),
        { code: '', all: [defaultCondition()] },
      ];
      props.onChange(next);
    } else {
      const next: ListingExclusionRule[] = [
        ...(value as ListingExclusionRule[]),
        { all: [defaultCondition()] },
      ];
      props.onChange(next);
    }
  };

  return (
    <Stack spacing={1}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <ToggleButtonGroup
          value={mode}
          exclusive
          size="small"
          onChange={(_, v) => v && handleModeChange(v)}
        >
          <ToggleButton value="form">Form</ToggleButton>
          <ToggleButton
            value="json"
            // Allow toggling to JSON freely; only the inbound direction
            // is gated (when JSON doesn't parse).
          >
            JSON
          </ToggleButton>
        </ToggleButtonGroup>
        {mode === 'json' && jsonError !== null && (
          <Typography variant="caption" color="error">
            {jsonError} — fix to switch back to form.
          </Typography>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {mode === 'form' && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addRule}
          >
            Add rule
          </Button>
        )}
      </Stack>

      {mode === 'json' ? (
        <TextField
          multiline
          minRows={kind === 'classification' ? 6 : 4}
          maxRows={20}
          value={jsonText}
          onChange={(e) => handleJsonChange(e.target.value)}
          error={jsonError !== null}
          helperText={jsonError ?? ''}
          sx={{ fontFamily: 'monospace' }}
        />
      ) : value.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          No rules yet. Click <strong>Add rule</strong> to create one.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {value.map((rule, idx) => (
            <RuleCard
              key={idx}
              kind={kind}
              rule={rule}
              index={idx}
              isFirst={idx === 0}
              isLast={idx === value.length - 1}
              onChange={(next) => updateRuleAt(idx, next)}
              onMoveUp={() => moveRule(idx, -1)}
              onMoveDown={() => moveRule(idx, +1)}
              onDuplicate={() => duplicateRule(idx)}
              onRemove={() => removeRuleAt(idx)}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// Per-rule card
// ---------------------------------------------------------------------------

interface RuleCardProps {
  kind: Kind;
  rule: ListingRule | ListingExclusionRule;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  onChange: (next: ListingRule | ListingExclusionRule) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDuplicate: () => void;
  onRemove: () => void;
}

const RuleCard: React.FC<RuleCardProps> = ({
  kind,
  rule,
  index,
  isFirst,
  isLast,
  onChange,
  onMoveUp,
  onMoveDown,
  onDuplicate,
  onRemove,
}) => {
  const conditions = rule.all ?? [];

  const updateCondition = (condIdx: number, next: ListingRuleCondition) => {
    const nextAll = [...conditions];
    nextAll[condIdx] = next;
    onChange({ ...rule, all: nextAll } as ListingRule | ListingExclusionRule);
  };

  const removeCondition = (condIdx: number) => {
    const nextAll = conditions.filter((_, i) => i !== condIdx);
    onChange({ ...rule, all: nextAll } as ListingRule | ListingExclusionRule);
  };

  const addCondition = () => {
    const nextAll = [...conditions, defaultCondition()];
    onChange({ ...rule, all: nextAll } as ListingRule | ListingExclusionRule);
  };

  const summary = summarizeRule(rule, kind);

  return (
    <Card variant="outlined">
      <CardContent sx={{ pb: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Chip size="small" label={`#${index + 1}`} />
          {kind === 'classification' && (
            <TextField
              size="small"
              label="Code"
              placeholder="HW.PC.Laptop"
              value={(rule as ListingRule).code ?? ''}
              onChange={(e) =>
                onChange({ ...(rule as ListingRule), code: e.target.value })
              }
              sx={{ width: 240 }}
            />
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Move up">
            <span>
              <IconButton size="small" onClick={onMoveUp} disabled={isFirst}>
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Move down">
            <span>
              <IconButton size="small" onClick={onMoveDown} disabled={isLast}>
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Duplicate">
            <IconButton size="small" onClick={onDuplicate}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete rule">
            <IconButton size="small" onClick={onRemove} color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        <Stack spacing={1}>
          {conditions.map((cond, ci) => (
            <ConditionRow
              key={ci}
              condition={cond}
              onChange={(next) => updateCondition(ci, next)}
              onRemove={() => removeCondition(ci)}
              canRemove={conditions.length > 1}
            />
          ))}
        </Stack>

        <Box sx={{ mt: 1 }}>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={addCondition}
          >
            Add condition
          </Button>
        </Box>

        <Divider sx={{ my: 1 }} />
        <Alert severity={summary.severity} sx={{ py: 0.25 }}>
          <Typography variant="body2">{summary.text}</Typography>
          {summary.hint && (
            <Typography variant="caption" color="text.secondary">
              {summary.hint}
            </Typography>
          )}
        </Alert>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Per-condition row
// ---------------------------------------------------------------------------

interface ConditionRowProps {
  condition: ListingRuleCondition;
  onChange: (next: ListingRuleCondition) => void;
  onRemove: () => void;
  canRemove: boolean;
}

const ConditionRow: React.FC<ConditionRowProps> = ({
  condition,
  onChange,
  onRemove,
  canRemove,
}) => {
  const fieldKnown = FIELD_OPTIONS.includes(condition.field);
  const opKnown = OP_OPTIONS.some((o) => o.op === condition.op);

  return (
    <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap" useFlexGap>
      <FormControl size="small" sx={{ minWidth: 180 }} error={!fieldKnown}>
        <InputLabel id={`field-label-${condition.field}`}>Field</InputLabel>
        <Select
          labelId={`field-label-${condition.field}`}
          label="Field"
          value={fieldKnown ? condition.field : ''}
          onChange={(e) =>
            onChange({ ...condition, field: e.target.value as ListingRuleField })
          }
        >
          {!fieldKnown && condition.field && (
            <MenuItem value="" disabled>
              <em>(unknown: {String(condition.field)})</em>
            </MenuItem>
          )}
          {FIELD_OPTIONS.map((f) => (
            <MenuItem key={f} value={f}>
              {f}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 200 }} error={!opKnown}>
        <InputLabel id={`op-label-${condition.field}-${condition.op}`}>Op</InputLabel>
        <Select
          labelId={`op-label-${condition.field}-${condition.op}`}
          label="Op"
          value={opKnown ? condition.op : ''}
          onChange={(e) => {
            const nextOp = e.target.value as ListingOperator;
            // Reshape value/values when the operator changes input mode so
            // we never carry a stale `value` when the user moves to a list
            // operator (which would survive serialisation but be ignored).
            const next: ListingRuleCondition = { ...condition, op: nextOp };
            if (OPS_WITH_SINGLE_VALUE.has(nextOp)) {
              delete next.values;
              if (next.value === undefined) next.value = '';
            } else if (OPS_WITH_VALUES_LIST.has(nextOp)) {
              delete next.value;
              if (!Array.isArray(next.values)) next.values = [];
            } else {
              delete next.value;
              delete next.values;
            }
            onChange(next);
          }}
        >
          {!opKnown && condition.op && (
            <MenuItem value="" disabled>
              <em>(unknown: {String(condition.op)})</em>
            </MenuItem>
          )}
          {OP_OPTIONS.map(({ op, label }) => (
            <MenuItem key={op} value={op}>
              {label} <Typography variant="caption" sx={{ ml: 0.5, color: 'text.secondary' }}>({op})</Typography>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {OPS_WITH_SINGLE_VALUE.has(condition.op) && (
        <TextField
          size="small"
          label="Value"
          value={condition.value ?? ''}
          onChange={(e) => onChange({ ...condition, value: e.target.value })}
          sx={{
            minWidth: 220,
            flexGrow: 1,
            ...(condition.op === 'regex' && { fontFamily: 'monospace' }),
          }}
        />
      )}
      {OPS_WITH_VALUES_LIST.has(condition.op) && (
        <Autocomplete
          multiple
          freeSolo
          size="small"
          options={[]}
          value={condition.values ?? []}
          onChange={(_, next) => onChange({ ...condition, values: next as string[] })}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                size="small"
                label={option}
                {...getTagProps({ index })}
                key={`${option}-${index}`}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Values"
              placeholder="Type and press Enter"
            />
          )}
          sx={{ minWidth: 280, flexGrow: 1 }}
        />
      )}
      {OPS_WITHOUT_INPUT.has(condition.op) && (
        <Box sx={{ flexGrow: 1 }} />
      )}

      <IconButton
        size="small"
        onClick={onRemove}
        disabled={!canRemove}
        color="error"
        title={canRemove ? 'Remove condition' : 'A rule needs at least one condition'}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// Plain-English summary
// ---------------------------------------------------------------------------

interface RuleSummary {
  text: string;
  severity: 'info' | 'warning' | 'error';
  hint?: string;
}

export function summarizeRule(
  rule: ListingRule | ListingExclusionRule,
  kind: Kind,
): RuleSummary {
  const conditions = rule.all ?? [];
  if (conditions.length === 0) {
    return {
      text: '(no conditions — this rule never matches)',
      severity: 'warning',
    };
  }

  const parts = conditions.map(describeCondition);
  const condSentence = parts.join(' AND ');

  let text: string;
  let severity: RuleSummary['severity'] = 'info';

  if (kind === 'exclusion') {
    text = `Drop rows where ${condSentence}.`;
  } else {
    const code = (rule as ListingRule).code?.trim();
    if (!code) {
      text = `Tag with (no code) when ${condSentence}.`;
      severity = 'warning';
    } else {
      text = `Tag as "${code}" when ${condSentence}.`;
    }
  }

  // Inverted-logic guard: a single-condition exclusion using `ne` against
  // a non-empty value almost always means the user wanted `eq` (drop only
  // matching rows) — `ne` drops the inverse, which feels backwards.
  // `not_in` is intentionally allowed because the contract documents it as
  // an allow-list pattern for exclusions.
  let hint: string | undefined;
  if (
    kind === 'exclusion' &&
    conditions.length === 1 &&
    conditions[0].op === 'ne' &&
    (conditions[0].value ?? '').trim() !== ''
  ) {
    hint =
      `Heads up: this drops every row whose ${conditions[0].field} is NOT ` +
      `"${conditions[0].value}" — i.e. it keeps only that value. If you ` +
      `meant to drop only that value, switch the op to "equals (eq)".`;
    severity = 'warning';
  }

  return { text, severity, hint };
}

function describeCondition(cond: ListingRuleCondition): string {
  const field = cond.field || '(no field)';
  switch (cond.op) {
    case 'eq':
      return `${field} equals "${cond.value ?? ''}"`;
    case 'ne':
      return `${field} does not equal "${cond.value ?? ''}"`;
    case 'contains':
      return `${field} contains "${cond.value ?? ''}"`;
    case 'regex':
      return `${field} matches /${cond.value ?? ''}/i`;
    case 'in':
      return `${field} is one of [${(cond.values ?? []).map(quote).join(', ')}]`;
    case 'not_in':
      return `${field} is not one of [${(cond.values ?? []).map(quote).join(', ')}]`;
    case 'contains_all':
      return `${field} contains all of [${(cond.values ?? []).map(quote).join(', ')}]`;
    case 'contains_any':
      return `${field} contains any of [${(cond.values ?? []).map(quote).join(', ')}]`;
    case 'is_null':
      return `${field} is empty`;
    case 'is_not_null':
      return `${field} is not empty`;
    default:
      return `${field} ${String(cond.op)} ?`;
  }
}

const quote = (s: string): string => `"${s}"`;

const defaultCondition = (): ListingRuleCondition => ({
  field: 'category',
  op: 'eq',
  value: '',
});
